import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

interface LotteryInfo {
  lotteryId: string;
  businessId: string;
  name: string;
  description?: string;
  startDate: string;
  endDate: string;
  prizePool: {
    totalPrizes: number;
    prizes: Array<{
      tier: string;
      quantity: number;
      description: string;
    }>;
  };
  eligibilityRules: {
    requireActivation: boolean;
    requireGPS: boolean;
    maxEntriesPerTag: number;
  };
  status: 'draft' | 'active' | 'ended' | 'cancelled';
  drawDate?: string;
  stats?: {
    totalEntries: number;
    uniqueParticipants: number;
    remainingPrizes: number;
  };
}

interface UserEntry {
  entryId: string;
  tagId: string;
  enteredAt: string;
  productName?: string;
  isWinner?: boolean;
  prize?: string;
}

// Countdown Timer Component
const CountdownTimer: React.FC<{ endDate: string }> = ({ endDate }) => {
  const [timeLeft, setTimeLeft] = useState({ 
    days: 0, 
    hours: 0, 
    minutes: 0, 
    seconds: 0 
  });
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const end = new Date(endDate).getTime();
      const diff = end - now;
      
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000),
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };
    
    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);
    
    return () => clearInterval(interval);
  }, [endDate]);
  
  if (timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0) {
    return <span className="text-red-600 font-semibold">Ended</span>;
  }
  
  return (
    <div className="flex items-center space-x-2">
      {timeLeft.days > 0 && (
        <div className="bg-purple-100 px-2 py-1 rounded text-xs font-bold text-purple-700">
          {timeLeft.days}d
        </div>
      )}
      <div className="bg-purple-100 px-2 py-1 rounded text-xs font-bold text-purple-700">
        {String(timeLeft.hours).padStart(2, '0')}h
      </div>
      <div className="bg-purple-100 px-2 py-1 rounded text-xs font-bold text-purple-700">
        {String(timeLeft.minutes).padStart(2, '0')}m
      </div>
      <div className="bg-purple-100 px-2 py-1 rounded text-xs font-bold text-purple-700">
        {String(timeLeft.seconds).padStart(2, '0')}s
      </div>
    </div>
  );
};

const ViewLottery: React.FC = () => {
  const navigate = useNavigate();
  const [lotteries, setLotteries] = useState<LotteryInfo[]>([]);
  const [userEntries, setUserEntries] = useState<UserEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLottery, setSelectedLottery] = useState<LotteryInfo | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("consumerToken");
    setIsAuthenticated(!!token);
  }, []);

  const loadLotteries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE}/consumer-lottery/active`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Failed to load lotteries: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.lotteries) {
        setLotteries(data.lotteries);
      } else {
        setLotteries([]);
      }
    } catch (err: any) {
      console.error("Error loading lotteries:", err);
      setError(err.message || "Failed to load lotteries");
      setLotteries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUserEntries = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const token = localStorage.getItem("consumerToken");
      const response = await fetch(`${API_BASE}/consumer-lottery/my-entries`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.entries) {
          setUserEntries(data.entries);
        }
      }
    } catch (err) {
      console.error("Error loading user entries:", err);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadLotteries();
    loadUserEntries();
  }, [loadLotteries, loadUserEntries]);

  // Fixed formatDate function
  const formatDate = (dateString: string) => {
    if (!dateString) return "Not set";
    
    try {
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', dateString);
        return "Invalid Date";
      }
      
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return "Invalid Date";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "ended":
        return "bg-gray-100 text-gray-800";
      case "draft":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const isLotteryActive = (lottery: LotteryInfo) => {
    const now = new Date();
    const start = new Date(lottery.startDate);
    const end = new Date(lottery.endDate);
    return lottery.status === "active" && now >= start && now <= end;
  };

  const calculateProgress = (lottery: LotteryInfo) => {
    if (!lottery.stats) return 0;
    const total = lottery.prizePool?.totalPrizes || 0;
    const remaining = lottery.stats.remainingPrizes || total;
    const claimed = total - remaining;
    return total > 0 ? (claimed / total) * 100 : 0;
  };

  const shareLottery = (lottery: LotteryInfo) => {
    const url = `${window.location.origin}/consumer/lottery/${lottery.lotteryId}`;
    const text = `Check out ${lottery.name} - ${lottery.prizePool.totalPrizes} prizes to win!`;
    
    if (navigator.share) {
      navigator.share({
        title: lottery.name,
        text: text,
        url: url,
      }).catch(() => {
        copyToClipboard(url);
      });
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Link copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy link');
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading lotteries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">🎁 Lottery Center</h1>
              <p className="text-purple-100">Join exciting prize draws and win amazing rewards!</p>
            </div>
            <div className="flex space-x-3">
              <Link
                to="/consumer/lottery-test"
                className="bg-white text-purple-600 px-6 py-3 rounded-lg font-semibold hover:bg-purple-50 transition-colors"
              >
                🧪 Test Page
              </Link>
              {!isAuthenticated ? (
                <button
                  onClick={() => navigate("/consumer/login")}
                  className="bg-purple-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-400 transition-colors"
                >
                  Login to Join
                </button>
              ) : (
                <button
                  onClick={() => {
                    localStorage.removeItem("consumerToken");
                    setIsAuthenticated(false);
                    setUserEntries([]);
                    window.location.reload();
                  }}
                  className="bg-purple-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-400 transition-colors"
                >
                  Logout
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Enhanced Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-xl flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-2xl mr-3">⚠️</span>
              <div>
                <div className="font-semibold">Error Loading Lotteries</div>
                <div className="text-sm">{error}</div>
              </div>
            </div>
            <button
              onClick={() => {
                setError(null);
                loadLotteries();
              }}
              className="bg-red-100 hover:bg-red-200 text-red-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-sm p-6 text-white transform hover:scale-105 transition-transform">
            <div className="text-3xl font-bold mb-1">
              {lotteries.filter(l => isLotteryActive(l)).length}
            </div>
            <div className="text-sm text-purple-100">Active Lotteries</div>
            <div className="text-xs text-purple-200 mt-1">
              {lotteries.length} total
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-sm p-6 text-white transform hover:scale-105 transition-transform">
            <div className="text-3xl font-bold mb-1">
              {lotteries.reduce((sum, l) => sum + (l.prizePool?.totalPrizes || 0), 0)}
            </div>
            <div className="text-sm text-green-100">Total Prizes</div>
            <div className="text-xs text-green-200 mt-1">
              {lotteries.reduce((sum, l) => sum + (l.stats?.remainingPrizes || 0), 0)} remaining
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-sm p-6 text-white transform hover:scale-105 transition-transform">
            <div className="text-3xl font-bold mb-1">{userEntries.length}</div>
            <div className="text-sm text-blue-100">Your Entries</div>
            <div className="text-xs text-blue-200 mt-1">
              {isAuthenticated ? 'Across lotteries' : 'Login to view'}
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-sm p-6 text-white transform hover:scale-105 transition-transform">
            <div className="text-3xl font-bold mb-1">
              {userEntries.filter(e => e.isWinner).length}
            </div>
            <div className="text-sm text-orange-100">Prizes Won</div>
            <div className="text-xs text-orange-200 mt-1">
              {isAuthenticated ? 'Your wins' : 'Login to participate'}
            </div>
          </div>
        </div>

        {/* My Entries Section */}
        {isAuthenticated && userEntries.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">My Lottery Entries</h2>
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tag ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entered At</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {userEntries.map((entry) => (
                      <tr key={entry.entryId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded">{entry.tagId}</code>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {entry.productName || "—"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(entry.enteredAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {entry.isWinner ? (
                            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              🏆 Winner: {entry.prize}
                            </span>
                          ) : (
                            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => navigate(`/consumer/lottery-result/${entry.tagId}`)}
                            className="text-purple-600 hover:text-purple-800 font-medium"
                          >
                            View Result →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Active Lotteries with Refresh Button */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">
            Active Lotteries
            <span className="ml-3 text-sm text-gray-500 font-normal">
              ({lotteries.length} {lotteries.length === 1 ? 'lottery' : 'lotteries'})
            </span>
          </h2>
          <button
            onClick={() => {
              setError(null);
              loadLotteries();
              loadUserEntries();
            }}
            className="flex items-center space-x-2 text-purple-600 hover:text-purple-800 font-medium bg-purple-50 px-4 py-2 rounded-lg hover:bg-purple-100 transition-colors"
          >
            <span>🔄</span>
            <span>Refresh</span>
          </button>
        </div>

        {lotteries.length === 0 ? (
          /* Enhanced Empty State */
          <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
            <div className="text-gray-400 text-6xl mb-4">🎲</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Lotteries</h3>
            <p className="text-gray-600 mb-6">
              Check back soon for exciting prize draws!
              {error && " There was an error loading lotteries."}
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => {
                  setError(null);
                  loadLotteries();
                }}
                className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
              >
                🔄 Refresh
              </button>
              <Link
                to="/consumer/lottery-test"
                className="inline-block bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Go to Test Page
              </Link>
            </div>
          </div>
        ) : (
          /* Lottery Cards Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lotteries.map((lottery) => (
              <div key={lottery.lotteryId} className="bg-white rounded-xl shadow-sm border hover:shadow-lg transition-shadow">
                {/* Card Header with Gradient */}
                <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-6 rounded-t-xl text-white">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-bold">{lottery.name}</h3>
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(lottery.status)}`}>
                      {lottery.status}
                    </span>
                  </div>
                  <p className="text-purple-100 text-sm">{lottery.description || "No description"}</p>
                </div>

                {/* Card Body */}
                <div className="p-6 space-y-4">
                  {/* Prize Pool */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">🏆 Prize Pool</h4>
                    <div className="text-xs text-gray-600">
                      Total: {lottery.prizePool?.totalPrizes || 0} prizes
                    </div>
                    
                    {/* Progress Bar */}
                    {lottery.stats && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>Claimed</span>
                          <span>
                            {lottery.prizePool.totalPrizes - lottery.stats.remainingPrizes} / {lottery.prizePool.totalPrizes}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${calculateProgress(lottery)}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Dates with Countdown */}
                  <div className="border-t pt-4">
                    <div className="text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Start:</span>
                        <span className="font-medium text-gray-900">{formatDate(lottery.startDate)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Ends in:</span>
                        <CountdownTimer endDate={lottery.endDate} />
                      </div>
                      {lottery.drawDate && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Draw:</span>
                          <span className="font-medium text-purple-600">{formatDate(lottery.drawDate)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  {lottery.stats && (
                    <div className="border-t pt-4">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <div className="text-lg font-bold text-gray-900">{lottery.stats.totalEntries}</div>
                          <div className="text-xs text-gray-500">Entries</div>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-gray-900">{lottery.stats.uniqueParticipants}</div>
                          <div className="text-xs text-gray-500">Players</div>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-gray-900">{lottery.stats.remainingPrizes}</div>
                          <div className="text-xs text-gray-500">Left</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Requirements */}
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Requirements</h4>
                    <div className="flex flex-wrap gap-2">
                      {lottery.eligibilityRules?.requireActivation && (
                        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">✓ Activation</span>
                      )}
                      {lottery.eligibilityRules?.requireGPS && (
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">📍 GPS</span>
                      )}
                      {lottery.eligibilityRules?.maxEntriesPerTag && (
                        <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded">
                          Max {lottery.eligibilityRules.maxEntriesPerTag} entries/tag
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="border-t pt-4 space-y-2">
                    <button
                      onClick={() => setSelectedLottery(lottery)}
                      className="w-full bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium"
                    >
                      View Details
                    </button>
                    {isLotteryActive(lottery) && (
                      <Link
                        to="/consumer/lottery-test"
                        className="block w-full text-center bg-pink-600 text-white px-4 py-3 rounded-lg hover:bg-pink-700 transition-colors font-medium"
                      >
                        Join Now →
                      </Link>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        shareLottery(lottery);
                      }}
                      className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                    >
                      📤 Share Lottery
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Details Modal (unchanged) */}
      {selectedLottery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-bold">{selectedLottery.name}</h2>
                <button
                  onClick={() => setSelectedLottery(null)}
                  className="text-white hover:text-gray-200 text-2xl"
                >
                  ✕
                </button>
              </div>
              <p className="text-purple-100">{selectedLottery.description || "No description"}</p>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">🏆 Prize Breakdown</h3>
                <div className="space-y-2">
                  {selectedLottery.prizePool?.prizes?.map((prize, idx) => (
                    <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-semibold text-gray-900">{prize.tier}</div>
                          <div className="text-sm text-gray-600">{prize.description}</div>
                        </div>
                        <div className="text-lg font-bold text-purple-600">{prize.quantity}x</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">📅 Timeline</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between p-3 bg-gray-50 rounded">
                    <span className="text-gray-600">Registration Opens:</span>
                    <span className="font-medium">{formatDate(selectedLottery.startDate)}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-gray-50 rounded">
                    <span className="text-gray-600">Registration Closes:</span>
                    <span className="font-medium">{formatDate(selectedLottery.endDate)}</span>
                  </div>
                  {selectedLottery.drawDate && (
                    <div className="flex justify-between p-3 bg-purple-50 rounded">
                      <span className="text-gray-600">Prize Draw:</span>
                      <span className="font-medium text-purple-600">{formatDate(selectedLottery.drawDate)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">📋 Eligibility Rules</h3>
                <ul className="space-y-2 text-sm">
                  {selectedLottery.eligibilityRules?.requireActivation && (
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span>Product must be activated to enter</span>
                    </li>
                  )}
                  {selectedLottery.eligibilityRules?.requireGPS && (
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span>GPS validation required at entry</span>
                    </li>
                  )}
                  {selectedLottery.eligibilityRules?.maxEntriesPerTag && (
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span>
                        Maximum {selectedLottery.eligibilityRules.maxEntriesPerTag} entries per product tag
                      </span>
                    </li>
                  )}
                </ul>
              </div>

              <div className="flex space-x-3 pt-4 border-t">
                {isLotteryActive(selectedLottery) ? (
                  <Link
                    to="/consumer/lottery-test"
                    className="flex-1 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors text-center font-medium"
                  >
                    Join This Lottery
                  </Link>
                ) : (
                  <div className="flex-1 bg-gray-100 text-gray-500 px-6 py-3 rounded-lg text-center font-medium cursor-not-allowed">
                    Lottery Not Active
                  </div>
                )}
                <button
                  onClick={() => setSelectedLottery(null)}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewLottery;